import { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Input,
  Modal,
  Select,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader/index.js";
import { DataTable } from "@/components/DataTable/DataTable.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { UnauthorizedView } from "@/components/UnauthorizedView/index.js";
import { useQuery, useMutation } from "../../../hooks/useApi.js";
import { useSSEStream } from "../../../hooks/useSSEStream.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  platformService,
  type FeatureFlag,
  type FeatureFlagHistoryEntry,
  type FeatureFlagHistoryValue,
  type FeatureFlagsResponse,
  type Rollout,
  type UpdateFeatureFlagConfig,
} from "../../../api/platform.service.js";
import { TargetingEditor } from "./TargetingEditor";
import {
  type TargetingRuleDraft,
  targetingSummary,
  targetingToText,
  toRuleDrafts,
  toTargetingPayload,
  validateTargeting,
} from "./targeting";
import styles from "./FeatureFlags.module.css";

const FALLBACK_REFRESH_INTERVAL = 30000;

function formatTimestamp(value?: string | null): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function StatusBadge({ status }: { status: string }) {
  const isActive = status === "active";
  return (
    <span
      className={`${styles.flagStatusBadge} ${isActive ? styles.flagActive : styles.flagStale}`}
    >
      {status}
    </span>
  );
}

const LOCKED_TOOLTIP =
  "This flag is locked in code and can only be changed by shipping a code change that sets locked:false.";

function LockedBadge() {
  return (
    <span className={styles.lockedBadge} title={LOCKED_TOOLTIP}>
      🔒 Locked
    </span>
  );
}

function ScopeBadges({ scopes }: { scopes: string[] }) {
  return (
    <div className={styles.scopeChips}>
      {scopes.map(scope => (
        <Chip key={scope} size="small" variant="filled">
          {scope}
        </Chip>
      ))}
    </div>
  );
}

function rolloutToText(rollout: Rollout | null): string {
  if (!rollout) return "Simple";
  return rollout.weightedVariants.map(v => `${v.variantKey}: ${v.weight}%`).join(", ");
}

function RolloutSummary({ rollout }: { rollout: Rollout | null }) {
  if (!rollout) return <span className={styles.simpleLabel}>Simple</span>;
  return <span className={styles.rolloutSummary}>{rolloutToText(rollout)}</span>;
}

const CHANGE_LABELS: Record<FeatureFlagHistoryEntry["changeType"], string> = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
};

function ChangeTypeBadge({ changeType }: { changeType: FeatureFlagHistoryEntry["changeType"] }) {
  const variantClass =
    changeType === "create"
      ? styles.changeCreate
      : changeType === "delete"
        ? styles.changeDelete
        : styles.changeUpdate;
  return (
    <span className={`${styles.changeBadge} ${variantClass}`}>{CHANGE_LABELS[changeType]}</span>
  );
}

type FlagValue = FeatureFlagHistoryValue | null;

function valueToText(value: FlagValue, fallback: string): string {
  if (!value) return fallback;
  const base = `enabled: ${value.enabled}, ${rolloutToText(value.rollout)}`;
  // Rows written before targeting existed have no `targeting` key.
  const targeting = targetingToText(value.targeting);
  return targeting ? `${base}, ${targeting}` : base;
}

type SaveError = { message: string; details: string[] };

function toSaveError(err: unknown): SaveError {
  if (!(err instanceof Error)) return { message: "Failed to update flag", details: [] };
  const raw = (err as Error & { details?: unknown }).details;
  const details = Array.isArray(raw)
    ? raw
        .map(d => {
          if (!d || typeof d !== "object") return null;
          const { path, message } = d as { path?: unknown; message?: unknown };
          if (typeof message !== "string") return null;
          return Array.isArray(path) && path.length > 0 ? `${path.join(".")}: ${message}` : message;
        })
        .filter((d): d is string => d !== null)
    : [];
  return { message: err.message || "Failed to update flag", details };
}

function HistoryDiff({
  previousValue,
  newValue,
}: {
  previousValue: FlagValue;
  newValue: FlagValue;
}) {
  const before = valueToText(previousValue, "service defaults");
  const after = valueToText(newValue, "reset to defaults");
  return (
    <span className={styles.diffCell}>
      <span>{before}</span>
      <span className={styles.diffArrow}> → </span>
      <span>{after}</span>
    </span>
  );
}

type EditModalProps = {
  flag: FeatureFlag;
  isOpen: boolean;
  onClose: () => void;
  /** Rejects with the API error when the save fails. */
  onSave: (key: string, config: UpdateFeatureFlagConfig) => Promise<void>;
  onReset: (key: string) => Promise<void>;
  saving: boolean;
  canWrite: boolean;
};

function EditModal({ flag, isOpen, onClose, onSave, onReset, saving, canWrite }: EditModalProps) {
  const [enabled, setEnabled] = useState(flag.effective?.enabled ?? false);
  const [weights, setWeights] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    const rollout = flag.effective?.rollout;
    if (rollout) {
      for (const wv of rollout.weightedVariants) {
        initial[wv.variantKey] = wv.weight;
      }
    } else {
      for (const v of flag.variants) {
        initial[v.key] = 0;
      }
      if (flag.variants.length > 0) {
        initial[flag.variants[0].key] = 100;
      }
    }
    return initial;
  });
  const [stickiness, setStickiness] = useState(flag.effective?.rollout?.stickiness ?? "clientId");
  const [seed, setSeed] = useState(flag.effective?.rollout?.seed ?? 1);
  const [useRollout, setUseRollout] = useState(!!flag.effective?.rollout);
  const [targetingRules, setTargetingRules] = useState<TargetingRuleDraft[]>(() =>
    toRuleDrafts(flag.effective?.targeting),
  );
  const [fallbackVariantKey, setFallbackVariantKey] = useState<string | null>(
    flag.effective?.targeting?.fallbackVariantKey ?? null,
  );
  const [showReseedConfirm, setShowReseedConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saveError, setSaveError] = useState<SaveError | null>(null);

  const locked = flag.locked === true;
  const editable = canWrite && !locked;

  const variantKeys = useMemo(() => flag.variants.map(v => v.key), [flag.variants]);

  const totalWeight = useMemo(
    () => Object.values(weights).reduce((sum, w) => sum + w, 0),
    [weights],
  );
  const targetingValidation = useMemo(
    () => validateTargeting(targetingRules, fallbackVariantKey, variantKeys),
    [targetingRules, fallbackVariantKey, variantKeys],
  );
  const isValid = (!useRollout || totalWeight === 100) && targetingValidation.isValid;

  const buildRollout = (rolloutSeed: number): Rollout => ({
    stickiness,
    seed: rolloutSeed,
    weightedVariants: Object.entries(weights).map(([variantKey, weight]) => ({
      variantKey,
      weight,
    })),
  });

  // What Save would send, used by the targeting help text.
  const formRollout = useRollout ? buildRollout(seed) : null;

  const submit = async (config: UpdateFeatureFlagConfig) => {
    setSaveError(null);
    try {
      await onSave(flag.key, config);
    } catch (err) {
      setSaveError(toSaveError(err));
    }
  };

  const handleSave = async () => {
    if (!editable || !isValid) return;
    await submit({
      enabled,
      rollout: formRollout,
      targeting: toTargetingPayload(targetingRules, fallbackVariantKey),
    });
  };

  const handleReseed = async () => {
    if (!editable || !isValid) return;
    setShowReseedConfirm(false);
    const newSeed = seed + 1;
    setSeed(newSeed);
    await submit({
      enabled,
      rollout: buildRollout(newSeed),
      targeting: toTargetingPayload(targetingRules, fallbackVariantKey),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit: ${flag.key}`}
      size="large"
      blurBackground
    >
      <div className={styles.editModal}>
        <div className={styles.modalHero}>
          <div>
            <h4 className={styles.modalHeroTitle}>{flag.name || flag.key}</h4>
            <p className={styles.modalHeroSubtitle}>
              {flag.description || "No description provided."}
            </p>
          </div>
          <div className={styles.modalHeroMeta}>
            <span className={styles.typeBadge}>{flag.flagType || "unknown"}</span>
            <StatusBadge status={flag.status} />
            {locked && <LockedBadge />}
          </div>
        </div>

        <div className={styles.editSection}>
          <h4 className={styles.editSectionTitle}>Flag Info</h4>
          <div className={styles.infoGridTwo}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Key</span>
              <span className={styles.infoValue}>
                <code>{flag.key}</code>
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Declared by</span>
              <span className={styles.infoValue}>{flag.declaredBy.join(", ") || "-"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Scopes</span>
              <span className={styles.infoValue}>{flag.scopes.join(", ") || "-"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Type</span>
              <span className={styles.infoValue}>{flag.flagType || "-"}</span>
            </div>
          </div>
        </div>

        {flag.defaults && (
          <div className={styles.editSection}>
            <h4 className={styles.editSectionTitle}>Service Defaults</h4>
            <div className={styles.infoGridTwo}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Enabled</span>
                <span className={styles.infoValue}>{flag.defaults.enabled ? "Yes" : "No"}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Rollout</span>
                <span className={styles.infoValue}>
                  <RolloutSummary rollout={flag.defaults.rollout} />
                </span>
              </div>
            </div>
          </div>
        )}

        <div className={styles.editSection}>
          <h4 className={styles.editSectionTitle}>Override Configuration</h4>

          <div className={styles.toggleCard}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={e => {
                  setEnabled(e.target.checked);
                  setSaveError(null);
                }}
                disabled={!editable}
              />
              <span>Enabled</span>
            </label>
            <span className={styles.toggleCardDescription}>
              Turns this flag on or off regardless of default configuration.
            </span>
          </div>

          <div className={styles.toggleCard}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={useRollout}
                onChange={e => {
                  setUseRollout(e.target.checked);
                  setSaveError(null);
                }}
                disabled={!editable}
              />
              <span>Use percentage rollout</span>
            </label>
            <span className={styles.toggleCardDescription}>
              Split traffic by weighted variants using sticky assignments.
            </span>
          </div>

          {useRollout && (
            <div className={styles.rolloutConfig}>
              <div className={styles.formField}>
                <Select
                  label="Stickiness"
                  value={stickiness}
                  onChange={e => {
                    setStickiness(e.target.value);
                    setSaveError(null);
                  }}
                  disabled={!editable}
                  options={[
                    { value: "clientId", label: "Client ID" },
                    { value: "sessionId", label: "Session ID" },
                    { value: "userId", label: "User ID" },
                  ]}
                  size="small"
                />
              </div>

              <div className={styles.rolloutHeader}>
                <div className={styles.seedPill}>Seed: {seed}</div>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowReseedConfirm(true)}
                  disabled={saving || !editable || !isValid}
                >
                  Reseed
                </Button>
              </div>

              <div className={styles.variantWeights}>
                <h5>Variant Weights</h5>
                {flag.variants.map(variant => (
                  <div key={variant.key} className={styles.variantRow}>
                    <span className={styles.variantKey}>{variant.name || variant.key}</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={weights[variant.key] ?? 0}
                      onChange={e => {
                        setWeights(prev => ({
                          ...prev,
                          [variant.key]: Number(e.target.value),
                        }));
                        setSaveError(null);
                      }}
                      disabled={!editable}
                      className={styles.weightInput}
                    />
                    <span className={styles.weightPercent}>%</span>
                    <div className={styles.weightBar}>
                      <div
                        className={styles.weightBarFill}
                        style={{ width: `${weights[variant.key] ?? 0}%` }}
                      />
                    </div>
                  </div>
                ))}
                <div
                  className={`${styles.totalRow} ${totalWeight !== 100 ? styles.totalInvalid : ""}`}
                >
                  Total: {totalWeight}% {totalWeight !== 100 && "(must be 100%)"}
                </div>
              </div>
            </div>
          )}

          {flag.override && (
            <div className={styles.infoGridTwo}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Override Created</span>
                <span className={styles.infoValue}>{formatTimestamp(flag.override.createdAt)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Override Updated</span>
                <span className={styles.infoValue}>{formatTimestamp(flag.override.updatedAt)}</span>
              </div>
            </div>
          )}
        </div>

        <TargetingEditor
          rules={targetingRules}
          fallbackVariantKey={fallbackVariantKey}
          onChange={(rules, fallback) => {
            setTargetingRules(rules);
            setFallbackVariantKey(fallback);
            setSaveError(null);
          }}
          variants={flag.variants}
          enabled={enabled}
          rollout={formRollout}
          disabled={!editable}
          validation={targetingValidation}
        />

        {locked && (
          <div className={styles.lockedNotice} title={LOCKED_TOOLTIP}>
            <LockedBadge /> {LOCKED_TOOLTIP}
          </div>
        )}

        {saveError && (
          <div className={styles.saveError} role="alert">
            <strong>Couldn't save: </strong>
            {saveError.message}
            {saveError.details.length > 0 && (
              <ul>
                {saveError.details.map(detail => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className={styles.editActions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isValid || saving || !editable}
          >
            {saving ? "Saving..." : "Save Override"}
          </Button>
          {flag.override && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setShowResetConfirm(true)}
              disabled={saving || !editable}
            >
              Reset to Defaults
            </Button>
          )}
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
      <Modal
        isOpen={showReseedConfirm}
        onClose={() => setShowReseedConfirm(false)}
        title="Reseed Rollout"
        size="small"
        blurBackground
      >
        <div className={styles.confirmModal}>
          <p>
            This will re-randomise all user assignments for this flag. Users may see a different
            variant on their next visit.
          </p>
          <div className={styles.confirmActions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleReseed}
              disabled={saving || !isValid}
            >
              Confirm Reseed
            </Button>
            <Button variant="text" onClick={() => setShowReseedConfirm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="Reset to Defaults"
        size="small"
        blurBackground
      >
        <div className={styles.confirmModal}>
          <p>This will delete the override and revert to service defaults.</p>
          <div className={styles.resetComparison}>
            <strong>Current Override</strong>
            <pre>{JSON.stringify(flag.override, null, 2)}</pre>
          </div>
          <div className={styles.resetComparison}>
            <strong>Service Defaults</strong>
            <pre>{JSON.stringify(flag.defaults, null, 2)}</pre>
          </div>
          <div className={styles.confirmActions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={async () => {
                await onReset(flag.key);
                setShowResetConfirm(false);
              }}
              disabled={saving}
            >
              Confirm Reset
            </Button>
            <Button variant="text" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}

type TabId = "configuration" | "history";

const TABS: { id: TabId; label: string }[] = [
  { id: "configuration", label: "Configuration" },
  { id: "history", label: "History" },
];

function FlagTabs({ activeTab, onChange }: { activeTab: TabId; onChange: (tab: TabId) => void }) {
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (e.key === "ArrowRight") nextIndex = (index + 1) % TABS.length;
    else if (e.key === "ArrowLeft") nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = TABS.length - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    const next = TABS[nextIndex];
    onChange(next.id);
    document.getElementById(`flag-tab-${next.id}`)?.focus();
  };

  return (
    <div className={styles.tabs} role="tablist" aria-label="Feature flags views">
      {TABS.map((tab, index) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`flag-tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`flag-panel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={e => handleKeyDown(e, index)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function FeatureFlagsPage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("platform.write");
  const [activeTab, setActiveTab] = useState<TabId>("configuration");
  const [scopeFilter, setScopeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FeatureFlag | null>(null);
  const [streamData, setStreamData] = useState<FeatureFlagsResponse | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const handleStreamData = useCallback((payload: FeatureFlagsResponse) => {
    setStreamError(null);
    setStreamData(payload);
  }, []);
  const handleStreamError = useCallback((streamErr: Error) => {
    setStreamError(streamErr.message);
  }, []);

  const { data, loading, error, refetch } = useQuery(() => platformService.getFeatureFlags(), []);
  const effectiveData = streamData ?? data;

  const { connected: streamConnected } = useSSEStream<FeatureFlagsResponse>({
    url: platformService.getFeatureFlagsStreamUrl(),
    enabled: true,
    isDataEvent: (payload: unknown): payload is FeatureFlagsResponse =>
      !!payload &&
      typeof payload === "object" &&
      Array.isArray((payload as { featureFlags?: unknown }).featureFlags),
    onData: handleStreamData,
    onError: handleStreamError,
  });

  useEffect(() => {
    if (streamConnected) return;

    const interval = setInterval(() => refetch(), FALLBACK_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refetch, streamConnected]);

  const { mutate: updateFlag, loading: updating } = useMutation(
    (args: { key: string; config: UpdateFeatureFlagConfig }) =>
      platformService.updateFeatureFlag(args.key, args.config),
  );

  const { mutate: deleteFlag, loading: deleting } = useMutation((key: string) =>
    platformService.deleteFeatureFlag(key),
  );

  // History tab state (append pagination)
  const [historyItems, setHistoryItems] = useState<FeatureFlagHistoryEntry[]>([]);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState<Error | null>(null);
  const [historyLoadedOnce, setHistoryLoadedOnce] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await platformService.getFeatureFlagHistory({ limit: 50 });
      setHistoryItems(res.items);
      setHistoryCursor(res.nextCursor);
      setHistoryLoadedOnce(true);
    } catch (err) {
      setHistoryError(err instanceof Error ? err : new Error("Failed to load history"));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadMoreHistory = useCallback(async () => {
    if (!historyCursor) return;
    setHistoryLoadingMore(true);
    try {
      const res = await platformService.getFeatureFlagHistory({
        before: historyCursor,
        limit: 50,
      });
      setHistoryItems(prev => [...prev, ...res.items]);
      setHistoryCursor(res.nextCursor);
    } catch (err) {
      setHistoryError(err instanceof Error ? err : new Error("Failed to load history"));
    } finally {
      setHistoryLoadingMore(false);
    }
  }, [historyCursor]);

  // Recent changes panel state (Configuration tab)
  const [recentItems, setRecentItems] = useState<FeatureFlagHistoryEntry[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<Error | null>(null);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const res = await platformService.getFeatureFlagHistory({ limit: 5 });
      setRecentItems(res.items);
    } catch (err) {
      setRecentError(err instanceof Error ? err : new Error("Failed to load recent changes"));
    } finally {
      setRecentLoading(false);
    }
  }, []);

  // Fetch recent changes on mount.
  useEffect(() => {
    void loadRecent();
  }, [loadRecent]);

  // Lazy-load history when the History tab first becomes active.
  useEffect(() => {
    if (activeTab === "history" && !historyLoadedOnce) {
      void loadHistory();
    }
  }, [activeTab, historyLoadedOnce, loadHistory]);

  // Refresh audit views after a successful mutation: always refresh the recent
  // panel, and refresh the history list too if it has already been loaded.
  const refreshAudit = useCallback(() => {
    void loadRecent();
    if (historyLoadedOnce) void loadHistory();
  }, [loadRecent, loadHistory, historyLoadedOnce]);

  const filteredFlags = useMemo(() => {
    if (!effectiveData?.featureFlags) return [];
    let result = effectiveData.featureFlags;

    if (scopeFilter) {
      result = result.filter(flag => flag.scopes.includes(scopeFilter));
    }
    if (statusFilter) {
      result = result.filter(flag => flag.status === statusFilter);
    }
    if (search) {
      const term = search.toLowerCase();
      result = result.filter(
        f => f.key.toLowerCase().includes(term) || (f.name && f.name.toLowerCase().includes(term)),
      );
    }

    return [...result].sort((a, b) =>
      a.key.localeCompare(b.key, undefined, { sensitivity: "base" }),
    );
  }, [effectiveData, search, scopeFilter, statusFilter]);

  // Keys of flags that are locked in code and cannot be changed via the API.
  const lockedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const flag of effectiveData?.featureFlags ?? []) {
      if (flag.locked) set.add(flag.key);
    }
    return set;
  }, [effectiveData]);

  const handleToggle = useCallback(
    async (flag: FeatureFlag) => {
      if (!canWrite || flag.locked) return;
      try {
        const newEnabled = !(flag.effective?.enabled ?? false);
        const rollout = flag.effective?.rollout ?? flag.defaults?.rollout ?? null;
        // Omitting `targeting` keeps the stored rules, which is what a quick toggle wants.
        await updateFlag({ key: flag.key, config: { enabled: newEnabled, rollout } });
        setToast({
          type: "success",
          message: `Flag '${flag.key}' ${newEnabled ? "enabled" : "disabled"}`,
        });
        refetch();
        refreshAudit();
      } catch (err) {
        setToast({
          type: "error",
          message: err instanceof Error ? err.message : "Failed to toggle flag",
        });
      }
    },
    [updateFlag, refetch, canWrite, refreshAudit],
  );

  // Errors propagate so the edit modal can show them next to the form.
  const handleSave = useCallback(
    async (key: string, config: UpdateFeatureFlagConfig) => {
      if (!canWrite || lockedKeys.has(key)) return;
      await updateFlag({ key, config });
      setToast({ type: "success", message: `Flag '${key}' updated` });
      setEditingFlag(null);
      refetch();
      refreshAudit();
    },
    [updateFlag, refetch, refreshAudit, canWrite, lockedKeys],
  );

  const handleReset = useCallback(
    async (key: string) => {
      if (!canWrite || lockedKeys.has(key)) return;
      try {
        await deleteFlag(key);
        setToast({ type: "success", message: `Flag '${key}' reset to defaults` });
        setEditingFlag(null);
        refetch();
        refreshAudit();
      } catch (err) {
        setToast({
          type: "error",
          message: err instanceof Error ? err.message : "Failed to reset flag",
        });
      }
    },
    [deleteFlag, refetch, refreshAudit, canWrite, lockedKeys],
  );

  const handleDelete = useCallback(
    async (flag: FeatureFlag) => {
      if (!canWrite || flag.locked) return;
      try {
        await deleteFlag(flag.key);
        setToast({
          type: "success",
          message: `Override for '${flag.key}' deleted`,
        });
        setConfirmDelete(null);
        refetch();
        refreshAudit();
      } catch (err) {
        setToast({
          type: "error",
          message: err instanceof Error ? err.message : "Failed to delete override",
        });
      }
    },
    [deleteFlag, refetch, refreshAudit, canWrite],
  );

  const columns: Column<FeatureFlag>[] = useMemo(
    () => [
      {
        header: "Enabled",
        accessor: "effective" as keyof FeatureFlag,
        render: (_value: unknown, row: FeatureFlag) => (
          <label
            className={styles.toggleSwitch}
            onClick={e => e.stopPropagation()}
            title={row.locked ? LOCKED_TOOLTIP : undefined}
          >
            <input
              type="checkbox"
              checked={row.effective?.enabled ?? false}
              onChange={() => handleToggle(row)}
              disabled={row.status === "stale" || row.locked === true || !canWrite}
            />
            <span className={styles.slider} />
          </label>
        ),
      },
      {
        header: "Key",
        accessor: "key" as keyof FeatureFlag,
        render: (value: unknown) => <code className={styles.flagKey}>{value as string}</code>,
      },
      {
        header: "Name",
        accessor: "name" as keyof FeatureFlag,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Type",
        accessor: "flagType" as keyof FeatureFlag,
        render: (value: unknown) =>
          value ? <span className={styles.typeBadge}>{value as string}</span> : "-",
      },
      {
        header: "Scopes",
        accessor: "scopes" as keyof FeatureFlag,
        render: (_value: unknown, row: FeatureFlag) => <ScopeBadges scopes={row.scopes} />,
      },
      {
        header: "Declared By",
        accessor: "declaredBy" as keyof FeatureFlag,
        render: (value: unknown) => <ScopeBadges scopes={value as string[]} />,
      },
      {
        header: "Rollout",
        accessor: "effective" as keyof FeatureFlag,
        render: (_value: unknown, row: FeatureFlag) => {
          const summary = targetingSummary(row.effective?.targeting);
          return (
            <>
              <RolloutSummary rollout={row.effective?.rollout ?? null} />
              {summary && (
                <span
                  className={styles.targetingSummary}
                  title={targetingToText(row.effective?.targeting) ?? undefined}
                >
                  {summary}
                </span>
              )}
            </>
          );
        },
      },
      {
        header: "Overridden",
        accessor: "override" as keyof FeatureFlag,
        render: (value: unknown, row: FeatureFlag) => (
          <div className={styles.scopeChips}>
            {value ? <span className={styles.overrideBadge}>Overridden</span> : null}
            {row.locked && <LockedBadge />}
          </div>
        ),
      },
      {
        header: "Status",
        accessor: "status" as keyof FeatureFlag,
        render: (value: unknown) => <StatusBadge status={value as string} />,
      },
      {
        header: "Actions",
        accessor: "key" as keyof FeatureFlag,
        render: (_value: unknown, row: FeatureFlag) => (
          <div className={styles.actionButtons} onClick={e => e.stopPropagation()}>
            {row.status === "active" && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setEditingFlag(row)}
                disabled={!canWrite || row.locked === true}
                title={row.locked ? LOCKED_TOOLTIP : undefined}
              >
                Edit
              </Button>
            )}
            {row.override && row.status === "active" && (
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => handleReset(row.key)}
                disabled={!canWrite || row.locked === true}
                title={row.locked ? LOCKED_TOOLTIP : undefined}
              >
                Reset
              </Button>
            )}
            {row.status === "stale" && (
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => setConfirmDelete(row)}
                disabled={!canWrite}
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [handleToggle, handleReset, canWrite],
  );

  const historyColumns: Column<FeatureFlagHistoryEntry>[] = useMemo(
    () => [
      {
        header: "When",
        accessor: "occurredAt" as keyof FeatureFlagHistoryEntry,
        render: (value: unknown) => formatTimestamp(value as string),
      },
      {
        header: "Who",
        accessor: "actorEmail" as keyof FeatureFlagHistoryEntry,
        render: (_value: unknown, row: FeatureFlagHistoryEntry) => row.actorEmail ?? row.actorId,
      },
      {
        header: "Flag",
        accessor: "flagKey" as keyof FeatureFlagHistoryEntry,
        render: (value: unknown) => <code className={styles.flagKey}>{value as string}</code>,
      },
      {
        header: "Change",
        accessor: "changeType" as keyof FeatureFlagHistoryEntry,
        render: (_value: unknown, row: FeatureFlagHistoryEntry) => (
          <ChangeTypeBadge changeType={row.changeType} />
        ),
      },
      {
        header: "Before → After",
        accessor: "newValue" as keyof FeatureFlagHistoryEntry,
        render: (_value: unknown, row: FeatureFlagHistoryEntry) => (
          <HistoryDiff previousValue={row.previousValue} newValue={row.newValue} />
        ),
      },
    ],
    [],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="Feature Flags"
          subtitle="Manage feature flag configurations and rollouts"
        />
      </div>

      <FlagTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "configuration" && (
        <div
          className={styles.tabPanel}
          role="tabpanel"
          id="flag-panel-configuration"
          aria-labelledby="flag-tab-configuration"
          tabIndex={0}
        >
          <div className={styles.recentChanges}>
            <div className={styles.recentChangesHeader}>
              <span>Recent changes</span>
              <Button variant="text" size="small" onClick={() => setActiveTab("history")}>
                View all changes
              </Button>
            </div>
            {recentLoading && recentItems.length === 0 ? (
              <div className={styles.recentChangesEmpty}>Loading recent changes…</div>
            ) : recentError ? (
              <div className={styles.recentChangesError}>
                Unable to load recent changes: {recentError.message}
              </div>
            ) : recentItems.length === 0 ? (
              <div className={styles.recentChangesEmpty}>No changes recorded.</div>
            ) : (
              <ul className={styles.recentChangesList}>
                {recentItems.map(entry => (
                  <li key={entry.id} className={styles.recentChangesItem}>
                    <span className={styles.recentChangesMeta}>
                      {formatTimestamp(entry.occurredAt)}
                    </span>
                    <span className={styles.recentChangesMeta}>·</span>
                    <span className={styles.recentChangesMeta}>
                      {entry.actorEmail ?? entry.actorId}
                    </span>
                    <span className={styles.recentChangesMeta}>·</span>
                    <code className={styles.flagKey}>{entry.flagKey}</code>
                    <ChangeTypeBadge changeType={entry.changeType} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.filtersContainer}>
            <div className={styles.filtersRow}>
              <div className={styles.filterDropdown}>
                <Input
                  placeholder="Search by key or name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className={`${styles.filterDropdown} ${styles.filterDropdownSmall}`}>
                <Select
                  label="Scope"
                  value={scopeFilter}
                  onChange={e => setScopeFilter(e.target.value)}
                  options={[
                    { value: "", label: "All Scopes" },
                    { value: "staff", label: "Staff" },
                    { value: "legal500", label: "Legal500" },
                    { value: "account", label: "Account" },
                  ]}
                  size="small"
                />
              </div>
              <div className={`${styles.filterDropdown} ${styles.filterDropdownSmall}`}>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  options={[
                    { value: "", label: "All Status" },
                    { value: "active", label: "Active" },
                    { value: "stale", label: "Stale" },
                  ]}
                  size="small"
                />
              </div>
            </div>
          </div>

          {loading && <LoadingState message="Loading feature flags..." />}

          {error && !loading && (
            <EmptyState
              variant="error"
              title="Failed to load feature flags"
              message={error.message}
              action={{ label: "Retry", onClick: () => refetch() }}
            />
          )}

          {!loading && !error && (
            <div className={styles.content}>
              {!filteredFlags.length ? (
                <EmptyState
                  variant="empty"
                  title="No feature flags found"
                  message="No feature flags match the current filters."
                />
              ) : (
                <DataTable data={filteredFlags} columns={columns} rowKey="key" size="small" />
              )}
            </div>
          )}
          {!loading && !error && streamError && (
            <div className={styles.streamWarning}>
              Live stream disconnected, using fallback refresh.
            </div>
          )}
        </div>
      )}

      {activeTab === "history" && (
        <div
          className={styles.tabPanel}
          role="tabpanel"
          id="flag-panel-history"
          aria-labelledby="flag-tab-history"
          tabIndex={0}
        >
          {historyLoading && historyItems.length === 0 ? (
            <LoadingState message="Loading change history..." />
          ) : historyError ? (
            <EmptyState
              variant="error"
              title="Failed to load change history"
              message={historyError.message}
              action={{ label: "Retry", onClick: () => void loadHistory() }}
            />
          ) : historyItems.length === 0 ? (
            <EmptyState
              variant="empty"
              title="No changes recorded"
              message="There are no feature flag changes to show yet."
            />
          ) : (
            <div className={styles.content}>
              <DataTable data={historyItems} columns={historyColumns} rowKey="id" size="small" />
              {historyCursor && (
                <div className={styles.loadMoreRow}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => void loadMoreHistory()}
                    disabled={historyLoadingMore}
                  >
                    {historyLoadingMore ? "Loading…" : "Load more"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {editingFlag && (
        <EditModal
          flag={editingFlag}
          isOpen={!!editingFlag}
          onClose={() => setEditingFlag(null)}
          onSave={handleSave}
          onReset={handleReset}
          saving={updating}
          canWrite={canWrite}
        />
      )}

      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete Stale Override"
        size="small"
        blurBackground
      >
        <div className={styles.confirmModal}>
          <p>
            This flag is no longer declared by any running service. Deleting will remove the stored
            override.
          </p>
          <p>
            <strong>Flag:</strong> <code>{confirmDelete?.key}</code>
          </p>
          <div className={styles.confirmActions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              disabled={deleting || !canWrite}
            >
              {deleting ? "Deleting..." : "Delete Override"}
            </Button>
            <Button variant="text" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </div>
  );
}

export default FeatureFlagsPage;
